import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scrollToSection, scrollToTop, setLenis } from './scroll';

gsap.registerPlugin(ScrollTrigger);

// The document remains the scroll container. Dialogs opt out so their own panels
// keep native scrolling, and the one Lenis instance owns all page scrolling.
export default function SmoothScroll() {
  const location = useLocation();

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    let lenis;
    let tick;
    const setup = () => {
      if (media.matches) return;
      // Lerp rather than a fixed duration: the page follows the wheel closely and
      // settles quickly, instead of every notch easing out over a full second.
      lenis = new Lenis({
        autoRaf: false,
        lerp: 0.14,
        smoothWheel: true,
        prevent: (node) => Boolean(node.closest('[data-lenis-prevent]')),
      });
      setLenis(lenis);
      lenis.on('scroll', ScrollTrigger.update);
      tick = (time) => lenis.raf(time * 1000);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
    };
    const teardown = () => {
      if (tick) gsap.ticker.remove(tick);
      lenis?.off('scroll', ScrollTrigger.update);
      lenis?.destroy();
      lenis = undefined;
      tick = undefined;
      setLenis(null);
    };
    const onMotionChange = () => {
      teardown();
      setup();
      ScrollTrigger.refresh();
    };
    setup();
    media.addEventListener('change', onMotionChange);
    return () => {
      media.removeEventListener('change', onMotionChange);
      teardown();
    };
  }, []);

  useEffect(() => {
    // Project/certificate URLs are overlays on the main page. Keep its position.
    if (/^\/(projects|certificates)\//.test(location.pathname)) return;
    if (location.pathname !== '/') {
      scrollToTop();
      return;
    }
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      requestAnimationFrame(() => scrollToSection(id));
    }
    requestAnimationFrame(() => ScrollTrigger.refresh());
  }, [location.pathname, location.hash]);

  return null;
}
